/**
 * Compact HTML-to-ARIA role mapping and accessible-name computation.
 * This is a pragmatic subset of the ARIA / accname specs — enough to produce
 * actionable snapshots, not spec-complete. See docs/PLAN.md §2.1.
 */
const INPUT_TYPE_ROLE = {
    button: "button",
    submit: "button",
    reset: "button",
    image: "button",
    checkbox: "checkbox",
    radio: "radio",
    range: "slider",
    search: "searchbox",
    email: "textbox",
    tel: "textbox",
    url: "textbox",
    text: "textbox",
    number: "spinbutton",
    password: "textbox",
};
const TAG_ROLE = {
    a: "link", // only when href present; handled below
    button: "button",
    select: "combobox",
    textarea: "textbox",
    h1: "heading",
    h2: "heading",
    h3: "heading",
    h4: "heading",
    h5: "heading",
    h6: "heading",
    nav: "navigation",
    main: "main",
    header: "banner",
    footer: "contentinfo",
    aside: "complementary",
    form: "form",
    ul: "list",
    ol: "list",
    li: "listitem",
    table: "table",
    img: "img",
    dialog: "dialog",
    fieldset: "group",
    section: "region",
};
/** Roles considered interactive/actionable for `interactive` snapshot mode. */
export const INTERACTIVE_ROLES = new Set([
    "button",
    "link",
    "checkbox",
    "radio",
    "textbox",
    "searchbox",
    "combobox",
    "listbox",
    "option",
    "slider",
    "spinbutton",
    "switch",
    "menuitem",
    "tab",
]);
/** Roles kept in interactive mode for structure/orientation. */
export const STRUCTURAL_KEEP = new Set([
    "heading",
    "navigation",
    "main",
    "banner",
    "contentinfo",
    "form",
    "dialog",
    "region",
    "alert",
]);
export function computeRole(el) {
    const explicit = el.getAttribute("role");
    if (explicit)
        return explicit.trim().split(/\s+/)[0] ?? "generic";
    const tag = el.tagName.toLowerCase();
    if (tag === "a")
        return el.hasAttribute("href") ? "link" : "generic";
    if (tag === "input") {
        const t = (el.getAttribute("type") ?? "text").toLowerCase();
        return INPUT_TYPE_ROLE[t] ?? "textbox";
    }
    return TAG_ROLE[tag] ?? "generic";
}
export function headingLevel(el) {
    const m = /^h([1-6])$/.exec(el.tagName.toLowerCase());
    if (m)
        return Number(m[1]);
    const aria = el.getAttribute("aria-level");
    if (aria && /^\d+$/.test(aria))
        return Number(aria);
    return undefined;
}
function textOf(el) {
    return (el?.textContent ?? "").replace(/\s+/g, " ").trim();
}
/** Pragmatic accessible-name algorithm. */
export function accessibleName(el) {
    // 1. aria-labelledby
    const labelledby = el.getAttribute("aria-labelledby");
    if (labelledby) {
        const root = el.getRootNode();
        const names = labelledby
            .split(/\s+/)
            .map((id) => textOf(root.getElementById?.(id) ?? null))
            .filter(Boolean);
        if (names.length)
            return names.join(" ");
    }
    // 2. aria-label
    const ariaLabel = el.getAttribute("aria-label");
    if (ariaLabel?.trim())
        return ariaLabel.trim();
    const tag = el.tagName.toLowerCase();
    // 3. Associated <label> for form controls
    if (tag === "input" || tag === "textarea" || tag === "select") {
        const id = el.getAttribute("id");
        if (id) {
            const root = el.getRootNode();
            const lbl = root.querySelector?.(`label[for="${cssEscape(id)}"]`);
            if (lbl)
                return textOf(lbl);
        }
        const wrapping = el.closest("label");
        if (wrapping)
            return textOf(wrapping);
        const ph = el.getAttribute("placeholder");
        if (ph?.trim())
            return ph.trim();
    }
    // 4. alt / title
    const alt = el.getAttribute("alt");
    if (alt?.trim())
        return alt.trim();
    // 5. Text content for named-from-content roles (buttons, links, headings…)
    const role = computeRole(el);
    if (["button", "link", "heading", "option", "menuitem", "tab", "listitem"].includes(role)) {
        const t = textOf(el);
        if (t)
            return t.slice(0, 120);
    }
    const title = el.getAttribute("title");
    if (title?.trim())
        return title.trim();
    return "";
}
/** Minimal escape for a string inside a quoted attribute selector. */
export function cssEscape(s) {
    return s.replace(/["\\]/g, "\\$&");
}
//# sourceMappingURL=roles.js.map